// IntegrationGate — pathless layout route.
// Redirects to /connect when the tenant has no active Stripe integration.
// Uses the same queryKey as IntegrationsCard (['dundial', 'integrations'])
// so the two components share the React Query cache — no duplicate fetch.

import { Navigate, Outlet } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ApiError, apiFetch } from "@avagency/frontend/lib/api";
import { ErrorState } from "@avagency/frontend/components/shell/ErrorState";
import { LoadingState } from "@avagency/frontend/components/shell/LoadingState";

interface Integration {
  id: string;
  provider: string;
  status: string;
}

interface IntegrationListResponse {
  integrations: Integration[];
}

export function IntegrationGate() {
  const q = useQuery<Integration[]>({
    queryKey: ["dundial", "integrations"],
    queryFn: async () => {
      try {
        const body = await apiFetch<IntegrationListResponse>(
          "/api/v1/integrations"
        );
        return body.integrations;
      } catch (err) {
        // 503 → OAuthStore not wired (dev / partial deploy).
        // Treat as empty so the gate passes, matching IntegrationsCard behaviour.
        if (err instanceof ApiError && err.status === 503) {
          return [];
        }
        throw err;
      }
    },
  });

  if (q.isLoading) return <LoadingState rows={3} />;

  // Render an inline error state rather than throwing — the outer route
  // tree has no error boundary, so throwing here would crash the whole app.
  if (q.error) {
    return <ErrorState error={q.error} retry={() => q.refetch()} />;
  }

  // Require an active Stripe integration specifically — another product's
  // active integration (e.g. HubSpot) must not bypass the connect gate.
  const hasActive = (q.data ?? []).some(
    (i) => i.provider === "stripe" && i.status === "active"
  );
  if (!hasActive) return <Navigate to="/connect" replace />;

  return <Outlet />;
}
